// pipe: frontend wave (F6) — the PURE, prop-only storefront Product-detail view
// (the Vitest-tested seam). Imports ONLY @affine/i18n, a TYPE-ONLY import from
// the storefront api service, the shared formatPrice helper (pure), and the css
// module — never the runtime service/auth/router/DI.
import { useI18n } from '@affine/i18n';

import type { StorefrontRecord } from '../services/storefront-api';
import { formatPrice } from './catalog-view';
import * as styles from './storefront.css';

export interface ProductDetailViewProps {
  /** The listing (Storefront positive-allowlist projection); null while loading. */
  row: StorefrontRecord | null;
  loading?: boolean;
  /** The listing is not available (sold/delisted/missing — server-uniform 404). */
  notFound?: boolean;
  error?: string | null;
  /** This listing is already a cart line (whole-listing purchase — one each). */
  inCart?: boolean;
  onAddToCart: () => void;
  onGoToCart?: () => void;
  onBack?: () => void;
}

function str(v: unknown): string | null {
  return v === undefined || v === null ? null : String(v);
}

function num(v: unknown): number | null {
  return typeof v === 'number' && Number.isFinite(v) ? v : null;
}

export function ProductDetailView(props: ProductDetailViewProps) {
  const t = useI18n();

  if (props.error) {
    return (
      <div className={styles.body}>
        <div className={styles.errorPanel} role="alert">
          {t['com.affine.storefront.product.error']()}
        </div>
      </div>
    );
  }

  if (props.notFound) {
    return (
      <div className={styles.body}>
        <div className={styles.statusPanel} role="status">
          {t['com.affine.storefront.product.not-found']()}
        </div>
      </div>
    );
  }

  if (!props.row) {
    return (
      <div className={styles.body}>
        <div className={styles.statusPanel} role="status">
          {t['com.affine.storefront.product.loading']()}
        </div>
      </div>
    );
  }

  const row = props.row;
  const headline = str(row.data.headline) ?? row.title ?? row.id.slice(0, 8);
  const price = formatPrice(num(row.data.ask_price), str(row.data.currency));
  const condition = str(row.data.condition);
  const sku = str(row.data.sku);
  const qty = num(row.data.quantity_available);
  // `spec` is a free-shape key/value object on the public allowlist.
  const spec =
    row.data.spec && typeof row.data.spec === 'object'
      ? Object.entries(row.data.spec as Record<string, unknown>)
      : [];

  return (
    <div className={styles.body}>
      {props.onBack ? (
        <button
          type="button"
          className={styles.linkButton}
          onClick={props.onBack}
        >
          {t['com.affine.storefront.product.back']()}
        </button>
      ) : null}
      <div className={styles.detailPanel}>
        <h1 className={styles.title}>{headline}</h1>
        {price ? <span className={styles.detailPrice}>{price}</span> : null}
        <div className={styles.specList}>
          {condition ? (
            <div className={styles.specRow}>
              <span className={styles.specName}>
                {t['com.affine.storefront.product.condition']()}
              </span>
              <span className={styles.specValue}>{condition}</span>
            </div>
          ) : null}
          {sku ? (
            <div className={styles.specRow}>
              <span className={styles.specName}>
                {t['com.affine.storefront.product.sku']()}
              </span>
              <span className={styles.specValue}>{sku}</span>
            </div>
          ) : null}
          {qty !== null ? (
            <div className={styles.specRow}>
              <span className={styles.specName}>
                {t['com.affine.storefront.product.available']()}
              </span>
              <span className={styles.specValue}>{String(qty)}</span>
            </div>
          ) : null}
        </div>
        {spec.length > 0 ? (
          <>
            <h2 className={styles.sectionTitle}>
              {t['com.affine.storefront.product.spec']()}
            </h2>
            <div className={styles.specList}>
              {spec.map(([k, v]) => (
                <div key={k} className={styles.specRow}>
                  <span className={styles.specName}>{k}</span>
                  <span className={styles.specValue}>{String(v)}</span>
                </div>
              ))}
            </div>
          </>
        ) : null}
        {props.inCart ? (
          <button
            type="button"
            className={styles.secondaryButton}
            onClick={props.onGoToCart}
          >
            {t['com.affine.storefront.product.in-cart']()}
          </button>
        ) : (
          <button
            type="button"
            className={styles.primaryButton}
            onClick={props.onAddToCart}
          >
            {t['com.affine.storefront.product.add-to-cart']()}
          </button>
        )}
      </div>
    </div>
  );
}
